import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Forbidden } from '@/routes/Forbidden';
import { formatDateTime } from '@/lib/format';
import {
  AUDIT_DEFAULT_TAKE,
  AUDIT_MAX_TAKE,
  AuditForbiddenError,
  mayHaveMore,
  useAuditTrail,
} from './audit/useAudit';

/**
 * What staff have done (M1-35). Application Administrators only.
 *
 * ⚠️ **The screen says when it may be showing you less than everything.** The
 * API clamps `take` at 200 and returns no total, so an over-large request comes
 * back 200 OK looking exactly like a complete answer. An audit screen that
 * silently truncates is worse than one that refuses to load: "I looked and
 * there was nothing after that" is a conclusion someone will act on.
 *
 * ⚠️ **On the display rule** — *identifiers and a persona, never a customer's
 * name* — which this story flags as the thing an audit screen leaks if nobody
 * is watching. `details` genuinely contains tenant names: `tenants.read`
 * records `name=…`, and `tenants.list` records the search term. That is the
 * trail doing its job, and an Application Administrator reading it can already
 * open the tenant page. What the rule governs is where those strings *go*, and
 * they go nowhere: nothing here is put in the URL, in a telemetry property, or
 * in an exception message. CC-17's redactor covers URLs; this screen simply
 * never creates one to redact.
 */
export function Audit() {
  const { t } = useTranslation();
  const [take, setTake] = useState(AUDIT_DEFAULT_TAKE);

  const trail = useAuditTrail(take);
  const rows = trail.data ?? [];

  if (trail.error instanceof AuditForbiddenError) {
    // The API's refusal, not the client guard's. Same screen, because it is
    // the same fact — but reached because the server said so, which is the
    // criterion this story cares about.
    return <Forbidden />;
  }

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('audit.title')}</h1>
        <p className="max-w-prose text-sm text-[var(--color-muted-foreground)]">
          {t('audit.subtitle')}
        </p>
      </div>

      {trail.isPending ? (
        <LoadingState label={t('audit.loading')} />
      ) : trail.isError ? (
        <ErrorState
          title={t('audit.loadErrorTitle')}
          description={t('audit.loadErrorDescription')}
          onRetry={() => void trail.refetch()}
        />
      ) : rows.length === 0 ? (
        // Only ever means "nothing has happened": the API clamps `take` up to 1
        // rather than honouring a zero, so an empty list can never be the
        // result of having asked for nothing.
        <EmptyState title={t('audit.emptyTitle')} description={t('audit.emptyDescription')} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <caption className="sr-only">{t('audit.tableCaption')}</caption>
              <thead className="border-b bg-[var(--color-muted)]">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('audit.columnWhen')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('audit.columnWho')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('audit.columnAction')}
                  </th>
                  <th scope="col" className="hidden px-4 py-2 text-left font-medium lg:table-cell">
                    {t('audit.columnDetails')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-b-0">
                    {/* `whitespace-nowrap` kept: at 375px the table scrolls
                        inside its own `overflow-x-auto` container either way —
                        the monospace staff id and the details string are what
                        force it, not this — and removing it only bought a
                        timestamp broken across four lines. The page itself
                        does not overflow, which is the rule that matters. */}
                    <td className="px-4 py-2 whitespace-nowrap">
                      {/* The viewer's clock, date and time from one formatter —
                          the same choice as every other screen here, so two
                          screens never date the same event differently. */}
                      {formatDateTime(row.createdAt)}
                    </td>
                    <td className="px-4 py-2">
                      {/* The person, then the authority they held AT THE TIME.
                          The name is resolved live and the persona is not, so a
                          promoted administrator's old rows still read as the
                          technician they were — see `StaffActionEntry`. */}
                      <span className="block font-medium">
                        {row.staffDisplayName ?? t('audit.unknownActor')}
                      </span>
                      <span className="block text-xs text-[var(--color-muted-foreground)]">
                        {t(`audit.persona.${row.persona}`, { defaultValue: row.persona })}
                      </span>
                      {/* The id stays, and is not decoration: it is the stable
                          identity anchor the name is only a label for. Two
                          people can share a display name; an offboarded actor
                          has none at all. */}
                      <span className="block font-mono text-xs break-all text-[var(--color-muted-foreground)]">
                        {row.staffUserId}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {/* `defaultValue` so an action added server-side appears
                          as its raw verb rather than as a blank cell — a new
                          kind of staff action must never be invisible here. */}
                      <span className="font-medium">
                        {t(`audit.action.${row.action}`, { defaultValue: row.action })}
                      </span>
                      {row.targetAccountId ? (
                        <Link
                          to={`/tenants/${row.targetAccountId}`}
                          className="block text-xs underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                        >
                          {t('audit.viewTenant')}
                        </Link>
                      ) : (
                        <span className="block text-xs text-[var(--color-muted-foreground)]">
                          {t('audit.noTenant')}
                        </span>
                      )}
                      {/* The details column is hidden below `lg`, so keep it
                          readable rather than merely invisible. */}
                      <span className="mt-1 block font-mono text-xs break-all text-[var(--color-muted-foreground)] lg:hidden">
                        {row.details}
                      </span>
                    </td>
                    <td className="hidden px-4 py-2 font-mono text-xs break-all text-[var(--color-muted-foreground)] lg:table-cell">
                      {row.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p
              role="status"
              aria-live="polite"
              className="text-sm text-[var(--color-muted-foreground)]"
            >
              {mayHaveMore(rows, take)
                ? // Hedged on purpose. The response carries no total, so this is
                  // inference from "I got everything I asked for" — and the
                  // failure to avoid is someone concluding they have seen it all.
                  t('audit.showingMaybeMore', { count: rows.length })
                : t('audit.showingAll', { count: rows.length })}
            </p>

            {mayHaveMore(rows, take) && take < AUDIT_MAX_TAKE ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setTake(AUDIT_MAX_TAKE)}
              >
                {t('audit.showMore', { max: AUDIT_MAX_TAKE })}
              </Button>
            ) : null}
          </div>

          {mayHaveMore(rows, take) && take >= AUDIT_MAX_TAKE ? (
            // The end of what this screen can reach, said plainly. Anything
            // older needs a query against the table, and pretending otherwise
            // is how an audit review reaches a wrong conclusion.
            <p className="max-w-prose text-sm text-[var(--color-muted-foreground)]">
              {t('audit.atMaximum', { max: AUDIT_MAX_TAKE })}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
